import React, { useState, useEffect } from 'react';
import { TargetSelect } from './TargetSelect';

const APISecurityScanner = () => {
  const [apiUrl, setApiUrl] = useState('');
  const [authToken, setAuthToken] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('form'); // 'form', 'history', 'results'
  const [scans, setScans] = useState([]);
  const [selectedScan, setSelectedScan] = useState(null);

  useEffect(() => {
    fetchScans();
  }, []);

  const fetchScans = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/scans/api-security', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setScans(data);
      }
    } catch (err) {
      console.error('Error fetching API security scans:', err);
    }
  };

  const handleScan = async () => {
    if (!apiUrl) {
      setError('Por favor ingrese la URL de la API');
      return;
    }

    setScanning(true);
    setError(null);
    setResults(null);

    try {
      const token = localStorage.getItem('token');
      
      const payload = {
        api_url: apiUrl,
        auth_token: authToken || null,
        include_auth_tests: true,
        include_injection_tests: true
      };
      
      const response = await fetch('/api/scans/api-security', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error ejecutando escaneo');
      }

      const data = await response.json();
      setResults(data.results);
      setViewMode('results');
      fetchScans();
    } catch (err) {
      setError(err.message);
    } finally {
      setScanning(false);
    }
  };

  const viewScanDetails = (scan) => {
    setSelectedScan(scan);
    setResults(scan.results);
    setViewMode('results');
  };

  const getRiskColor = (score) => {
    if (score >= 80) return { bg: 'bg-green-50', border: 'border-green-500', text: 'text-green-700', badge: 'bg-green-500' };
    if (score >= 60) return { bg: 'bg-yellow-50', border: 'border-yellow-500', text: 'text-yellow-700', badge: 'bg-yellow-500' };
    if (score >= 40) return { bg: 'bg-orange-50', border: 'border-orange-500', text: 'text-orange-700', badge: 'bg-orange-500' };
    return { bg: 'bg-red-50', border: 'border-red-500', text: 'text-red-700', badge: 'bg-red-500' };
  };

  const getRiskLabel = (riskLevel) => {
    const labels = {
      'LOW': '✅ BAJO RIESGO',
      'MEDIUM': '⚠️ RIESGO MEDIO',
      'HIGH': '🔴 ALTO RIESGO',
      'CRITICAL': '🚨 CRÍTICO'
    };
    return labels[riskLevel] || riskLevel;
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'CRITICAL': 'bg-red-100 text-red-800 border-red-300',
      'HIGH': 'bg-orange-100 text-orange-800 border-orange-300',
      'MEDIUM': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'LOW': 'bg-blue-100 text-blue-800 border-blue-300'
    };
    return colors[severity] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getOwaspStatusColor = (status) => {
    const colors = {
      'PASSED': 'bg-green-100 text-green-800',
      'LOW_RISK': 'bg-blue-100 text-blue-800',
      'MEDIUM_RISK': 'bg-yellow-100 text-yellow-800',
      'HIGH_RISK': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          🔌 Scanner de Seguridad de APIs
        </h1>
        <p className="text-lg text-gray-600">
          Análisis completo de seguridad basado en OWASP API Security Top 10 2023
        </p>
      </div>

      {/* View Mode Tabs */}
      <div className="mb-6 flex gap-4">
        <button
          onClick={() => setViewMode('form')}
          className={`px-6 py-3 rounded-lg font-semibold transition-all ${
            viewMode === 'form'
              ? 'bg-purple-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          🚀 Nuevo Escaneo
        </button>
        <button
          onClick={() => setViewMode('history')}
          className={`px-6 py-3 rounded-lg font-semibold transition-all ${
            viewMode === 'history'
              ? 'bg-purple-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          📋 Historial ({scans.length})
        </button>
      </div>

      {/* Info Banner */}
      {viewMode === 'form' && (
        <div className="mb-8 bg-purple-50 border-l-4 border-purple-500 p-6 rounded-lg">
          <div className="flex items-start">
            <span className="text-3xl mr-4">🔍</span>
            <div>
              <h3 className="text-lg font-semibold text-purple-900 mb-2">Qué Analiza este Scanner</h3>
              <ul className="text-purple-800 text-sm space-y-1">
                <li>• <strong>BOLA/IDOR:</strong> Acceso no autorizado a objetos de otros usuarios</li>
                <li>• <strong>Autenticación:</strong> Rate limiting, enumeración de usuarios, tokens</li>
                <li>• <strong>Autorización:</strong> Métodos HTTP no autorizados (DELETE, PUT sin auth)</li>
                <li>• <strong>Datos Sensibles:</strong> Exposición de credenciales, PII, tokens</li>
                <li>• <strong>Configuración:</strong> CORS, headers de seguridad, modo debug</li>
                <li>• <strong>Inventario:</strong> Endpoints no documentados, versiones antiguas</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Scan Form */}
      {viewMode === 'form' && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <div className="space-y-4">
            <TargetSelect
              label="URL Base de la API *"
              value={apiUrl}
              onChange={setApiUrl}
              types={['domain', 'ip']}
              asUrl
              allowPort
              disabled={scanning}
              helpText="Si la API no está en 80/443 (turneros, tablets, paneles internos), indicá el puerto. La autorización se valida por host, el puerto no la afecta."
              testid="api-target-select"
            />

            {/* Advanced Options */}
            <div>
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="text-purple-600 hover:text-purple-800 text-sm font-medium"
              >
                {showAdvanced ? '▼ Ocultar opciones avanzadas' : '▶ Mostrar opciones avanzadas'}
              </button>
            </div>

            {showAdvanced && (
              <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Token de Autenticación (opcional)
                  </label>
                  <input
                    type="password"
                    value={authToken}
                    onChange={(e) => setAuthToken(e.target.value)}
                    placeholder="Bearer token o API key"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    disabled={scanning}
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Si la API requiere autenticación, ingrese el token para realizar tests más completos
                  </p>
                </div>
              </div>
            )}

            <div className="pt-4">
              <button
                onClick={handleScan}
                disabled={scanning}
                className="w-full px-8 py-4 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200 text-lg"
              >
                {scanning ? '⏳ Escaneando API...' : '🔍 Iniciar Análisis de Seguridad'}
              </button>
            </div>
          </div>

          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              ❌ {error}
            </div>
          )}
        </div>
      )}

      {/* Loading State */}
      {scanning && (
        <div className="bg-white rounded-lg shadow-lg p-12 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
          <p className="text-xl font-semibold text-gray-900">Analizando seguridad de la API...</p>
          <p className="text-gray-600 mt-2">Esto puede tomar 1-2 minutos dependiendo del tamaño de la API</p>
          <div className="mt-6 text-sm text-gray-500">
            <p>• Descubriendo endpoints...</p>
            <p>• Probando autenticación y autorización...</p>
            <p>• Verificando configuración de seguridad...</p>
          </div>
        </div>
      )}

      {/* History View */}
      {viewMode === 'history' && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Historial de Escaneos de API Security
          </h3>

          {scans.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No hay escaneos registrados aún.</p>
              <button
                onClick={() => setViewMode('form')}
                className="mt-4 text-purple-600 hover:underline"
              >
                Crear primer escaneo →
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {scans.map((scan) => {
                const apiScore = scan.results?.api_security_score || 0;
                const riskLevel = scan.results?.risk_level || 'UNKNOWN';
                const endpointsFound = scan.results?.discovered_endpoints || 0;
                return (
                  <div
                    key={scan.id}
                    className="border border-gray-200 rounded-lg p-4 hover:border-purple-400 cursor-pointer transition"
                    onClick={() => viewScanDetails(scan)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900">{scan.target}</h4>
                        <p className="text-sm text-gray-500">
                          {new Date(scan.created_at).toLocaleString('es-AR')} • {endpointsFound} endpoints
                        </p>
                      </div>
                      <div className="text-right">
                        <div className={`text-2xl font-bold ${getRiskColor(apiScore).text}`}>
                          {apiScore}/100
                        </div>
                        <p className={`text-xs px-2 py-1 rounded ${getRiskColor(apiScore).bg}`}>
                          {getRiskLabel(riskLevel)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Results View */}
      {viewMode === 'results' && results && (
        <div className="space-y-8">
          {/* Back Button */}
          <button
            onClick={() => setViewMode('history')}
            className="text-purple-600 hover:text-purple-800 font-semibold"
          >
            ← Volver al historial
          </button>

          {/* Score Principal */}
          <div className={`rounded-lg shadow-xl p-8 border-2 ${getRiskColor(results.api_security_score).border} ${getRiskColor(results.api_security_score).bg}`}>
            <div className="text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Seguridad de API - OWASP Top 10
              </h2>
              <div className={`text-9xl font-bold mb-4 ${getRiskColor(results.api_security_score).text}`}>
                {results.api_security_score}
                <span className="text-4xl text-gray-500">/100</span>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-8 mb-6">
                <div
                  className={`h-8 rounded-full transition-all duration-500 ${getRiskColor(results.api_security_score).badge} flex items-center justify-end pr-4`}
                  style={{ width: `${results.api_security_score}%` }}
                >
                  <span className="text-white font-bold text-sm">{results.api_security_score}%</span>
                </div>
              </div>

              {/* Risk Badge */}
              <div className={`inline-block px-8 py-4 rounded-full font-bold text-2xl ${getRiskColor(results.api_security_score).badge} text-white shadow-lg`}>
                {getRiskLabel(results.risk_level)}
              </div>

              {/* Quick Stats */}
              <div className="mt-6 grid grid-cols-3 gap-4">
                <div className="bg-white/50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-gray-800">{results.discovered_endpoints}</div>
                  <div className="text-sm text-gray-600">Endpoints Descubiertos</div>
                </div>
                <div className="bg-white/50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-gray-800">{results.vulnerabilities?.length || 0}</div>
                  <div className="text-sm text-gray-600">Vulnerabilidades</div>
                </div>
                <div className="bg-white/50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-gray-800">{results.recommendations?.length || 0}</div>
                  <div className="text-sm text-gray-600">Recomendaciones</div>
                </div>
              </div>
            </div>
          </div>

          {/* OWASP API Top 10 Compliance */}
          {results.owasp_api_compliance && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="text-3xl mr-3">📋</span>
                Cumplimiento OWASP API Top 10 2023
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(results.owasp_api_compliance).map(([code, data]) => (
                  <div key={code} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <span className="font-mono text-sm text-purple-600">{code}</span>
                        <h4 className="font-medium text-gray-900">{data.name}</h4>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${getOwaspStatusColor(data.status)}`}>
                        {data.status === 'PASSED' ? '✓ OK' : data.status.replace('_', ' ')}
                      </span>
                    </div>
                    {data.risk_score > 0 && (
                      <div className="text-sm text-gray-500">
                        Puntos de riesgo: {data.risk_score}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Vulnerabilities */}
          {results.vulnerabilities && results.vulnerabilities.length > 0 && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="text-3xl mr-3">🛡️</span>
                Vulnerabilidades Detectadas ({results.vulnerabilities.length})
              </h3>
              <div className="space-y-4">
                {results.vulnerabilities.map((vuln, index) => (
                  <div
                    key={index}
                    className={`border-l-4 rounded-lg p-4 ${
                      vuln.severity === 'CRITICAL' ? 'border-red-500 bg-red-50' :
                      vuln.severity === 'HIGH' ? 'border-orange-500 bg-orange-50' :
                      vuln.severity === 'MEDIUM' ? 'border-yellow-500 bg-yellow-50' :
                      'border-blue-500 bg-blue-50'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-semibold text-gray-900">{vuln.title}</h4>
                      <span className={`px-2 py-1 rounded text-xs font-bold border ${getSeverityColor(vuln.severity)}`}>
                        {vuln.severity}
                      </span>
                    </div>
                    {vuln.owasp_api && (
                      <p className="text-sm font-mono text-purple-600 mb-2">{vuln.owasp_api}</p>
                    )}
                    <p className="text-sm text-gray-700 mb-3">{vuln.description}</p>
                    {vuln.endpoint && (
                      <p className="text-sm text-gray-500 mb-2">
                        <strong>Endpoint:</strong> <code className="bg-gray-200 px-1 rounded">{vuln.endpoint}</code>
                      </p>
                    )}
                    {vuln.recommendation && (
                      <div className="mt-3 p-3 bg-white/80 rounded border border-gray-200">
                        <p className="text-sm text-gray-600">
                          <strong>💡 Recomendación:</strong> {vuln.recommendation}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Detailed Results */}
          {results.detailed_results && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="text-3xl mr-3">📊</span>
                Resultados Detallados
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Endpoint Discovery */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">🔍 Descubrimiento de Endpoints</h4>
                  <div className="space-y-2 text-sm">
                    <p><strong>Total descubiertos:</strong> {results.detailed_results.endpoint_discovery?.total_discovered || 0}</p>
                    <p><strong>Swagger/OpenAPI:</strong> {results.detailed_results.endpoint_discovery?.swagger_found ? '✅ Encontrado' : '❌ No encontrado'}</p>
                    <p><strong>Métodos:</strong> {results.detailed_results.endpoint_discovery?.methods_found?.join(', ') || 'N/A'}</p>
                  </div>
                </div>

                {/* Authentication */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">🔐 Autenticación</h4>
                  <div className="space-y-2 text-sm">
                    <p><strong>Nivel de riesgo:</strong> 
                      <span className={`ml-2 px-2 py-1 rounded ${
                        results.detailed_results.authentication?.risk_level === 'CRITICAL' ? 'bg-red-100 text-red-800' :
                        results.detailed_results.authentication?.risk_level === 'HIGH' ? 'bg-orange-100 text-orange-800' :
                        results.detailed_results.authentication?.risk_level === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {results.detailed_results.authentication?.risk_level || 'N/A'}
                      </span>
                    </p>
                    <p><strong>Endpoints de auth:</strong> {results.detailed_results.authentication?.auth_endpoints_found?.length || 0}</p>
                    {results.detailed_results.authentication?.issues?.length > 0 && (
                      <div className="mt-2">
                        <p className="font-medium">Problemas:</p>
                        <ul className="list-disc list-inside text-red-600">
                          {results.detailed_results.authentication.issues.map((issue, i) => (
                            <li key={i}>{issue}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                {/* Rate Limiting */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">⏱️ Rate Limiting</h4>
                  <div className="space-y-2 text-sm">
                    <p><strong>Detectado:</strong> {results.detailed_results.rate_limiting?.rate_limited ? '✅ Sí' : '❌ No'}</p>
                    <p><strong>Nivel de riesgo:</strong> 
                      <span className={`ml-2 px-2 py-1 rounded ${
                        results.detailed_results.rate_limiting?.risk_level === 'HIGH' ? 'bg-red-100 text-red-800' :
                        results.detailed_results.rate_limiting?.risk_level === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {results.detailed_results.rate_limiting?.risk_level || 'N/A'}
                      </span>
                    </p>
                  </div>
                </div>

                {/* CORS */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">🌐 Configuración CORS</h4>
                  <div className="space-y-2 text-sm">
                    <p><strong>CORS habilitado:</strong> {results.detailed_results.cors_analysis?.cors_enabled ? '✅ Sí' : '❌ No'}</p>
                    <p><strong>Permite cualquier origen:</strong> {results.detailed_results.cors_analysis?.allow_all_origins ? '⚠️ Sí' : '✅ No'}</p>
                    <p><strong>Permite credenciales:</strong> {results.detailed_results.cors_analysis?.credentials_allowed ? '⚠️ Sí' : '✅ No'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Recommendations */}
          {results.recommendations && results.recommendations.length > 0 && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="text-3xl mr-3">💡</span>
                Recomendaciones Priorizadas
              </h3>
              <div className="space-y-4">
                {results.recommendations.map((rec, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                      rec.severity === 'CRITICAL' ? 'bg-red-500' :
                      rec.severity === 'HIGH' ? 'bg-orange-500' :
                      rec.severity === 'MEDIUM' ? 'bg-yellow-500' :
                      'bg-blue-500'
                    }`}>
                      {rec.priority}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">{rec.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{rec.action}</p>
                      {rec.owasp_reference && (
                        <p className="text-xs font-mono text-purple-600 mt-2">{rec.owasp_reference}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default APISecurityScanner;
